import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';
import excelIcon from '../../assets/icons/excel.png';
import { Tooltip } from "@mui/material";
import AppDataGrid from '../../components/common/AppDataGrid';

const ActiveUsersReport = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [filterText, setFilterText] = useState('');

  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [summaryCounts, setSummaryCounts] = useState({
    total: 0,
    installed: 0,
    uninstalled: 0
  });

  const [form, setForm] = useState({
    phonenumber: '',
    class_id: '',
    section_id: '',
    is_active: 'ALL'
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (form.class_id) {
      fetchSections(form.class_id);
    } else {
      setSections([]);
      setForm(prev => ({ ...prev, section_id: '' }));
    }
  }, [form.class_id]);

  const fetchClasses = async () => {
    try {
      const res = await axios.post(baseUrl + '/classes/', {
        action: 'READ',
        school_id: userObj.school_id
      });
      setClasses(res.data || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const res = await axios.post(baseUrl + '/Sections/', {
        action: 'DROPDOWNREAD',
        class_id,
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
      });
      setSections(res.data || []);
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value ?? '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      school_id: userObj.school_id,
      phonenumber: form.phonenumber || '',
      class_id: form.class_id ? parseInt(form.class_id) : null,
      section_id: form.section_id ? parseInt(form.section_id) : null,
      isactive: form.is_active || null
    };

    try {
      const res = await axios.post(baseUrl + '/activeusers/', payload);
      const data = res.data || [];

      // Counts
      let totalCount = 0;
      let activeCount = 0;
      let inactiveCount = 0;
      data.forEach(user => {
        if (user.firstname !== 'No Records Found') {
          totalCount++;
          if (user.status === 'Active') activeCount++;
          if (user.status === 'Inactive') inactiveCount++;
        }
      });

      setSummaryCounts({
        total: totalCount,
        installed: activeCount,
        uninstalled: inactiveCount
      });

      // If no rows, leave array empty; we'll show a no-rows overlay in the grid
      setUsers(data.length ? data : []);
    } catch (err) {
      toast.error("Error fetching users!");
      console.error("Fetch error:", err);
    }
  };

  const exportToExcel = () => {
    if (users.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const formatted = users.map((row) => ({
      "Student Name": `${row.student_first_name || ''} ${row.student_last_name || ''}`.trim(),
      "Father Name": row.father_name || '',
      "Mother Name": row.mother_name || '',
      "Father Phone": row.father_phonenumber || '',
      "Mother Phone": row.mother_phonenumber || '',
      "Class": row.class_name || '',
      "Section": row.section_name || '',
      "Status": row.status === 'Active' ? 'Installed' : 'Not Installed'
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "UsersReport");
    XLSX.writeFile(wb, "Active_Users_Report.xlsx");
  };

  const exportToPDF = () => {
    if (users.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const doc = new jsPDF();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.text("Active Users Report", 14, 15);

    const columns = [
      "Student Name",
      "Father Name",
      "Mother Name",
      "Father Phone",
      "Mother Phone",
      "Class",
      "Section",
      "Status"
    ];

    const rows = users.map((row) => [
      `${row.student_first_name || ''} ${row.student_last_name || ''}`.trim(),
      row.father_name || '',
      row.mother_name || '',
      row.father_phonenumber || '',
      row.mother_phonenumber || '',
      row.class_name || '',
      row.section_name || '',
      row.status === 'Active' ? 'Installed' : 'Not Installed'
    ]);

    autoTable(doc, {
      startY: 20,
      head: [columns],
      body: rows,
      styles: { fontSize: 10 },
    });

    doc.save("Active_Users_Report.pdf");
  };

  // Filter logic preserved
  const filteredUsers = users.filter(user => {
    const search = filterText.toLowerCase();
    return (
      `${user.student_first_name || ''} ${user.student_last_name || ''}`.toLowerCase().includes(search) ||
      (user.father_name || '').toLowerCase().includes(search) ||
      (user.mother_name || '').toLowerCase().includes(search) ||
      (user.father_phonenumber || '').toLowerCase().includes(search) ||
      (user.mother_phonenumber || '').toLowerCase().includes(search) ||
      (user.class_name || '').toLowerCase().includes(search) ||
      (user.section_name || '').toLowerCase().includes(search) ||
      (user.status || '').toLowerCase().includes(search)
    );
  });

  // ===== MUI DataGrid-style columns (centered + Tooltip) =====
  const columns = [
    {
      field: "student_name",
      headerName: "Student Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) =>
        `${params.row?.student_first_name || ''} ${params.row?.student_last_name || ''}`.trim(),
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "father_name",
      headerName: "Father Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "father_phonenumber",
      headerName: "Father Phone",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "mother_name",
      headerName: "Mother Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "mother_phonenumber",
      headerName: "Mother Phone",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "class_name",
      headerName: "Class",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "section_name",
      headerName: "Section",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: true,
      valueGetter: (params) =>
        params.row?.status === 'Active' ? 'Installed' : 'Not Installed',
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
  ];

  // Unique row id
  const getRowId = (row) =>
    row.id ??
    row.student_id ??
    `${row.student_first_name || ''}-${row.student_last_name || ''}-${row.father_phonenumber || ''}-${row.mother_phonenumber || ''}`;

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body className="pageBody pt-3">
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>Active Users Report</h6>
                </Col>
              </Row>

              <Row className="g-2 align-items-end flex-wrap">
                <Col xs={12} md={6} lg={2}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Class</Form.Label>
                      <Form.Select id='class_id' value={form.class_id} onChange={handleInputChange}>
                        <option value='' disabled hidden>Select Class</option>
                        {classes.map(cls => (
                          <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={2}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Section</Form.Label>
                      <Form.Select id='section_id' value={form.section_id} onChange={handleInputChange} disabled={!form.class_id}>
                        <option value='' disabled hidden>Select Section</option>
                        {sections.map(sec => (
                          <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={2}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Is Active</Form.Label>
                      <Form.Select id='is_active' value={form.is_active} onChange={handleInputChange}>
                        <option value='ALL'>ALL</option>
                        <option value='Y'>Installed</option>
                        <option value='N'>Not Installed</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={2}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control id='phonenumber' value={form.phonenumber} onChange={handleInputChange} placeholder='Enter Phone Number' />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} lg={4} className="d-flex justify-content-lg-end justify-content-start mt-2 mt-lg-0">
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn me-2"
                    onClick={() => setForm({ phonenumber: '', class_id: '', section_id: '', is_active: 'ALL' })}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="btn-success primaryBtn"
                  >
                    Search
                  </Button>
                </Col>
              </Row>

            </form>
          </Card.Body>
        </Card>

        <Row className='mt-3 mb-2 align-items-center'>
          <Col xs={12} md={6} className="text-start mb-2 mb-md-0">
            <div style={{ fontWeight: 'bold', fontSize: '15px' }}>
              Total Users: {summaryCounts.total} &nbsp; | &nbsp;
              Installed Count: {summaryCounts.installed} &nbsp; | &nbsp;
              Not Updated Count: {summaryCounts.uninstalled}
            </div>
          </Col>
          <Col className="d-flex justify-content-end">
            <Form.Control
              type="text"
              placeholder="Search..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="me-2 mb-2 mb-md-0"
              style={{ maxWidth: 220 }}
            />
            <Button variant="success" onClick={exportToExcel} className="me-2">
              <img src={excelIcon} alt="excel" style={{ width: 20, marginRight: 5 }} />
            </Button>
            <Button variant="danger" onClick={exportToPDF}>
              <FaFilePdf style={{ marginRight: 5 }} />
            </Button>
          </Col>
        </Row>

        <div className="commonTable height100 mt-2">
          <div className="tableBody">
            <AppDataGrid
              rows={filteredUsers.length ? filteredUsers : []}
              columns={columns}
              getRowId={getRowId}
             pageSizeOptions={[10, 20, 50]} 
              showToolbar={false}
              pagination
              density="compact"
              slots={{ noRowsOverlay: () => <div style={{ padding: 16, color: 'red' }}>No Records Found</div> }}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ActiveUsersReport;
